/**
 * Row and header actions menu (TMMPDM-0053). One icon button, "Actions for {{name}}",
 * aria-haspopup="menu". Edit details first, then today's terms and lifecycle items, a divider,
 * then destructive items. Returns nothing when there are no items.
 */
import { useId, useState, type ReactNode } from 'react';
import { Divider, IconButton, MenuItem, MenuList, Popover } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useCopy } from '@/hooks/useCopy';

export interface RowActionItem {
  key: string;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface RowActionsMenuProps {
  name: string;
  ariaLabel?: string;
  items: readonly RowActionItem[];
  /** Sits under the list, outside it (not a menu item). */
  note?: ReactNode;
  /** Disables the icon while a write from this menu's page is in flight. */
  disabled?: boolean;
}

export function RowActionsMenu({
  name,
  ariaLabel,
  items,
  note,
  disabled = false,
}: RowActionsMenuProps) {
  const { t } = useCopy();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const regular = items.filter((item) => !item.destructive);
  const destructive = items.filter((item) => item.destructive);
  if (items.length === 0) return null;

  const label = ariaLabel ?? t('components.actionsFor', 'Actions for {{name}}', { name });
  const close = () => setAnchor(null);
  const choose = (item: RowActionItem) => {
    close();
    item.onSelect();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={label}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={anchor ? true : undefined}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuList id={menuId} autoFocusItem aria-label={label}>
          {regular.map((item) => (
            <MenuItem
              key={item.key}
              disabled={item.disabled}
              onClick={(event) => {
                event.stopPropagation();
                choose(item);
              }}
            >
              {item.label}
            </MenuItem>
          ))}
          {regular.length > 0 && destructive.length > 0 ? <Divider /> : null}
          {destructive.map((item) => (
            <MenuItem
              key={item.key}
              disabled={item.disabled}
              onClick={(event) => {
                event.stopPropagation();
                choose(item);
              }}
              sx={{ color: 'error.main' }}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
        {note}
      </Popover>
    </>
  );
}
